import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { AccountBrandingService } from '@/services/branding/AccountBrandingService';
import { GenieBranding } from '@/types/ui/branding';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Hook to manage account-level branding
 * This branding acts as the default template for newly created Genies
 */
export const useAccountBranding = () => {
  const { team } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ['account-branding', team?.id];

  // Listen for branding updates from other team members and invalidate our cache
  const teamIdRef = useRef(team?.id);
  teamIdRef.current = team?.id;

  useEffect(() => {
    const channel = supabase.channel('team-branding-updates').on(
      'broadcast',
      { event: 'branding_updated' },
      ({ payload }: { payload: { teamId: number; updatedBy: string } }) => {
        if (payload.teamId === teamIdRef.current) {
          queryClient.invalidateQueries({ queryKey: ['account-branding', payload.teamId] });
        }
      },
    ).subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [queryClient]);

  // Query: Fetch account branding
  const {
    data: branding,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!team?.id) return null;
      return AccountBrandingService.getAccountBranding(team.id);
    },
    enabled: !!team?.id,
  });

  // Mutation: Update account branding
  const updateMutation = useMutation({
    mutationFn: async (newBranding: GenieBranding) => {
      if (!team?.id) throw new Error('No team found');
      await AccountBrandingService.updateAccountBranding(team.id, newBranding);
      return newBranding;
    },
    onSuccess: (newBranding) => {
      queryClient.setQueryData(queryKey, newBranding);
      toast.success('Account branding saved');
    },
    onError: (error) => {
      console.error('Error updating account branding:', error);
      toast.error('Failed to save account branding');
    },
  });

  // Mutation: Clear account branding
  const clearMutation = useMutation({
    mutationFn: async () => {
      if (!team?.id) throw new Error('No team found');
      await AccountBrandingService.clearAccountBranding(team.id);
    },
    onSuccess: () => {
      queryClient.setQueryData(queryKey, null);
      toast.success('Account branding cleared');
    },
    onError: (error) => {
      console.error('Error clearing account branding:', error);
      toast.error('Failed to clear account branding');
    },
  });

  return {
    branding,
    isLoading,
    error,
    hasAccountBranding: !!branding,
    updateBranding: updateMutation.mutate,
    clearBranding: clearMutation.mutate,
    isUpdating: updateMutation.isPending,
    isClearing: clearMutation.isPending,
  };
};
